import { useMutation, useQueryClient } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import {
  type MutationContext,
  entityActivitiesQueryKeys,
  libraryCompanyPeopleQueryKeys,
  libraryPeopleQueryKeys,
} from '@/hooks/library/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { EntityPerson, UpdateEntityPerson } from '@/types/library/Entity'

interface UseUpdatePersonArgs {
  orgId: UUID | ''
  companyId?: UUID | ''
  invalidateOnSuccess?: boolean
  optimisticUpdate?: boolean
  updateOnSuccess?: boolean
}

export const updatePerson = async (
  orgId: UUID,
  person: EntityPerson,
  accessToken: string | undefined | null,
): Promise<void> => {
  if (accessToken === null || accessToken === undefined) {
    throw new Error(`No access token provided.`)
  }
  const body: UpdateEntityPerson = {
    entity_type: 'person',
    first_name: person.first_name,
    middle_name: person.middle_name,
    last_name: person.last_name,
    preferred_name: person.preferred_name,
    suffixes: person.suffixes,
    aliases: person.aliases,
    role: person.role,
    email: person.email,
    alternate_email: person.alternate_email,
    phone: person.phone,
    mobile_phone: person.mobile_phone,
    office_phone: person.office_phone,
    linkedin_url: person.linkedin_url,
    // Convert empty strings to null for UUID fields (runtime safety)
    entity_company_id:
      (person.entity_company_id as string | null) === ''
        ? null
        : person.entity_company_id,
    entity_agency_id:
      (person.entity_agency_id as string | null | undefined) === ''
        ? null
        : person.entity_agency_id,
    entity_department_id:
      (person.entity_department_id as string | null) === ''
        ? null
        : person.entity_department_id,
    resume_file_id:
      (person.resume_file_id as string | null) === ''
        ? null
        : person.resume_file_id,
    description: person.description,
    is_internal: person.is_internal,
    relationship_status: person.relationship_status,
    owner_user_id: person.owner_user_id,
    priority: person.priority,
    last_contacted_at: person.last_contacted_at,
    tag_ids: person.tag_ids,
  }
  const res: Response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/entities/${person.id}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  )
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  }
}

export const useUpdatePerson = ({
  orgId,
  companyId,
  invalidateOnSuccess = true,
  optimisticUpdate = true,
  updateOnSuccess = false,
}: UseUpdatePersonArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<void, Error, EntityPerson, MutationContext<EntityPerson>>({
    mutationFn: (person) => updatePerson(orgId as UUID, person, accessToken),
    onMutate: async (person) => {
      const key = libraryPeopleQueryKeys.organization({ orgId })
      await queryClient.cancelQueries({ queryKey: key })

      const previousData =
        queryClient.getQueryData<PaginatedResponse<EntityPerson>>(key)

      if (previousData && optimisticUpdate) {
        const updatedData = previousData.data.map((p) =>
          p.id === person.id
            ? {
                ...person,
              }
            : p,
        )
        queryClient.setQueryData<PaginatedResponse<EntityPerson>>(key, {
          ...previousData,
          data: updatedData,
        })
      }

      const individualKey = libraryPeopleQueryKeys.person({
        orgId,
        personId: person.id,
      })
      await queryClient.cancelQueries({ queryKey: individualKey })

      const individualData =
        queryClient.getQueryData<EntityPerson>(individualKey)
      if (individualData && optimisticUpdate) {
        queryClient.setQueryData(individualKey, {
          ...individualData,
          ...person,
        })
      }

      return { list: previousData, entity: individualData }
    },
    onError: (err, person, context) => {
      if (context) {
        const key = libraryPeopleQueryKeys.organization({ orgId })
        queryClient.setQueryData(key, context.list)
        const individualKey = libraryPeopleQueryKeys.person({
          orgId,
          personId: person.id,
        })
        queryClient.setQueryData(individualKey, context.entity)
      }
      handleError(err)
    },
    onSuccess: (data, variables) => {
      if (updateOnSuccess) {
        const key = libraryPeopleQueryKeys.organization({ orgId })
        queryClient.setQueryData(
          key,
          (old: PaginatedResponse<EntityPerson>) => ({
            ...old,
            data: old.data.map((p) => {
              if (p.id === variables.id) {
                return {
                  ...p,
                  ...variables,
                }
              }
              return p
            }),
          }),
        )

        const individualKey = libraryPeopleQueryKeys.person({
          orgId,
          personId: variables.id,
        })
        queryClient.setQueryData(individualKey, variables)

        if (companyId) {
          const companyKey = libraryCompanyPeopleQueryKeys.company({
            orgId,
            companyId: companyId as UUID,
          })
          queryClient.setQueryData(
            companyKey,
            (old: PaginatedResponse<EntityPerson>) => ({
              ...old,
              data: old.data.map((p) =>
                p.id === variables.id ? variables : p,
              ),
            }),
          )
        }
      }

      if (invalidateOnSuccess) {
        const key = libraryPeopleQueryKeys.organization({ orgId })
        const individualKey = libraryPeopleQueryKeys.person({
          orgId,
          personId: variables.id,
        })
        const entityActivitiesKey = entityActivitiesQueryKeys.entity({
          orgId,
          entityId: variables.id,
        })
        return Promise.all([
          queryClient.invalidateQueries({ queryKey: individualKey }),
          queryClient.invalidateQueries({ queryKey: key }),
          queryClient.invalidateQueries({ queryKey: entityActivitiesKey }),
          companyId
            ? queryClient.invalidateQueries({
                queryKey: libraryCompanyPeopleQueryKeys.company({
                  orgId,
                  companyId: companyId as UUID,
                }),
              })
            : null,
        ])
      }
    },
  })
}
